/**
 * Pause/resume live-sync arrives as UserProfileUpdateEvent (not only
 * ShiftActionPerformed). Workline rows keep a copy of shift status as
 * `event_type`, which otherwise stays stale until the 30s poll.
 */

import {
    laterTimestamp,
    normalizeWorklineDateString,
} from "../Components/Common/Composables/memberShiftAvatarState.js";

/**
 * Capture shift fields before `replaceProfile` / `replaceUserProfile`.
 * Those store methods mutate the same profile object in place, so passing
 * the live store reference as `previous` would always look unchanged.
 *
 * @param {object|null|undefined} profile
 * @returns {{
 *   shift_status: mixed,
 *   recent_shift_event_at: mixed,
 *   shift_resume_at: mixed,
 * }|null}
 */
export function snapshotLiveSyncShiftFields(profile) {
    if (!profile) {
        return null;
    }

    return {
        shift_status: profile.shift_status ?? null,
        recent_shift_event_at: profile.recent_shift_event_at ?? null,
        shift_resume_at: profile.shift_resume_at ?? null,
    };
}

/**
 * @param {object|null|undefined} previous
 * @param {object|null|undefined} next
 * @returns {boolean}
 */
export function liveSyncShiftFieldsChanged(previous, next) {
    if (!next) {
        return false;
    }

    return (
        (previous?.shift_status ?? null) !== (next.shift_status ?? null) ||
        (previous?.recent_shift_event_at ?? null) !==
            (next.recent_shift_event_at ?? null) ||
        (previous?.shift_resume_at ?? null) !== (next.shift_resume_at ?? null)
    );
}

/**
 * @param {object} row
 * @param {object} profile
 * @returns {object}
 */
function patchedWorklineRowShiftFields(row, profile) {
    const next = { ...row };

    if (profile.shift_status != null) {
        next.event_type = profile.shift_status;
        next.shift_status = profile.shift_status;
    }

    if (profile.recent_shift_event_at != null) {
        const mergedRecent = laterTimestamp(
            profile.recent_shift_event_at,
            row.last_event_timestamp ?? row.recent_shift_event_at,
        );
        if (mergedRecent) {
            next.recent_shift_event_at = mergedRecent;
            next.last_event_timestamp = mergedRecent;
        }
    }

    if (Object.hasOwn(profile, "shift_resume_at")) {
        next.shift_resume_at = profile.shift_resume_at;
    }

    return next;
}

function patchedWorklineRow(row, profile) {
    const next = patchedWorklineRowShiftFields(row, profile);

    if (Object.hasOwn(profile, "active_task")) {
        next.active_task = profile.active_task;
    }

    if (Object.hasOwn(profile, "paused_task")) {
        next.paused_task = profile.paused_task;
    }

    return next;
}

function rowLastEventAt(row) {
    return row?.last_event_timestamp ?? row?.recent_shift_event_at ?? null;
}

/**
 * True when the cached user profile has a shift event strictly after the workline row.
 * Equal timestamps are treated as tied — the API row wins so stale profile tasks are not applied.
 *
 * @param {object} profile
 * @param {object} row
 */
export function profileShiftEventIsNewerThanRow(profile, row) {
    const profileAt = normalizeWorklineDateString(profile?.recent_shift_event_at);
    const rowAt = normalizeWorklineDateString(rowLastEventAt(row));

    if (!profileAt) {
        return false;
    }

    if (!rowAt) {
        return true;
    }

    if (profileAt === rowAt) {
        return false;
    }

    return (
        laterTimestamp(profile.recent_shift_event_at, rowLastEventAt(row)) ===
        profile.recent_shift_event_at
    );
}

/**
 * After `/shift-timer/shift`, prefer newer profile shift snapshots over stale rows.
 *
 * @param {(id: number) => object|null|undefined} getProfileById
 * @param {object[]|null|undefined} rows
 * @returns {{ rows: object[], changed: boolean }}
 */
export function reconcileWorklineRowsWithProfiles(rows, getProfileById) {
    if (!Array.isArray(rows) || !rows.length) {
        return { rows: rows ?? [], changed: false };
    }

    let changed = false;
    const nextRows = rows.map((row) => {
        const profileId = Number(row?.user_profile_id ?? row?.employee_id);
        if (!Number.isFinite(profileId)) {
            return row;
        }

        const profile = getProfileById(profileId);
        if (!profile || !profileShiftEventIsNewerThanRow(profile, row)) {
            return row;
        }

        changed = true;

        return patchedWorklineRowShiftFields(row, profile);
    });

    return { rows: nextRows, changed };
}

/**
 * @param {{
 *   usr_profile_logged_time?: object[],
 *   shift_timer_division_groups?: object[],
 * }} appState
 * @param {(id: number) => object|null|undefined} getProfileById
 * @returns {boolean}
 */
export function reconcileWorklineStateWithProfiles(appState, getProfileById) {
    if (!appState) {
        return false;
    }

    const flat = reconcileWorklineRowsWithProfiles(
        appState.usr_profile_logged_time,
        getProfileById,
    );
    if (flat.changed) {
        appState.usr_profile_logged_time = flat.rows;
    }

    const groups = appState.shift_timer_division_groups;
    if (!Array.isArray(groups) || !groups.length) {
        return flat.changed;
    }

    let groupsChanged = false;
    const nextGroups = groups.map((group) => {
        let groupChanged = false;
        let head = group?.head ?? null;

        if (head) {
            const patchedHead = reconcileWorklineRowsWithProfiles(
                [head],
                getProfileById,
            );
            if (patchedHead.changed) {
                head = patchedHead.rows[0];
                groupChanged = true;
            }
        }

        const membersResult = reconcileWorklineRowsWithProfiles(
            group?.members,
            getProfileById,
        );
        if (membersResult.changed) {
            groupChanged = true;
        }

        if (!groupChanged) {
            return group;
        }

        groupsChanged = true;

        return {
            ...group,
            head,
            members: membersResult.changed
                ? membersResult.rows
                : group.members,
        };
    });

    if (groupsChanged) {
        appState.shift_timer_division_groups = nextGroups;
    }

    return flat.changed || groupsChanged;
}

/**
 * Optimistic pause for the current user (web shift-return dialog).
 *
 * @param {{
 *   usr_profile_logged_time?: object[],
 *   shift_timer_division_groups?: object[],
 * }} appState
 * @param {number|string} userProfileId
 * @param {string} pauseEventType
 */
export function applyLocalShiftPauseToWorkline(
    appState,
    userProfileId,
    pauseEventType,
) {
    if (!appState || userProfileId == null) {
        return;
    }

    const profileId = Number(userProfileId);
    if (!Number.isFinite(profileId)) {
        return;
    }

    const patchRow = (row) => {
        if (!rowMatchesProfile(row, profileId)) {
            return row;
        }

        return {
            ...row,
            event_type: pauseEventType,
            shift_status: pauseEventType,
            paused_task: row.active_task,
            active_task: null,
        };
    };

    if (Array.isArray(appState.usr_profile_logged_time)) {
        appState.usr_profile_logged_time =
            appState.usr_profile_logged_time.map(patchRow);
    }

    if (!Array.isArray(appState.shift_timer_division_groups)) {
        return;
    }

    appState.shift_timer_division_groups =
        appState.shift_timer_division_groups.map((group) => {
            const head = group?.head ? patchRow(group.head) : group?.head ?? null;
            const members = Array.isArray(group?.members)
                ? group.members.map(patchRow)
                : group?.members;

            if (head === group?.head && members === group?.members) {
                return group;
            }

            return {
                ...group,
                head,
                members,
            };
        });
}

/**
 * After `/shift-timer/shift` poll + workline reconcile, push row shift/task
 * fields onto matching User store profiles so they stay in sync without
 * write-on-read in `getShiftInUsers`.
 *
 * Always assigns the five profile-aligned keys (null when absent on the row).
 *
 * @param {object[]|null|undefined} rows
 * @param {(id: number) => object|null|undefined} getProfileById
 * @param {{
 *   taskStart: string,
 *   taskStop: string,
 *   taskPause: string,
 *   isShiftRunning: (status: mixed) => boolean,
 *   isShiftPauseOrUnreachableOrInActive: (status: mixed) => boolean,
 *   getAssignerProfile?: (id: number) => object|null|undefined,
 * }} taskStatusHelpers
 * @returns {number} count of profiles updated
 */
export function syncWorklineRowsOntoUserProfiles(
    rows,
    getProfileById,
    taskStatusHelpers,
) {
    if (!Array.isArray(rows) || !rows.length || typeof getProfileById !== "function") {
        return 0;
    }

    const {
        taskStart,
        taskStop,
        taskPause,
        isShiftRunning,
        isShiftPauseOrUnreachableOrInActive,
        getAssignerProfile,
    } = taskStatusHelpers ?? {};

    let updated = 0;

    for (const row of rows) {
        const profileId = Number(row?.user_profile_id ?? row?.employee_id);
        if (!Number.isFinite(profileId)) {
            continue;
        }

        const profile = getProfileById(profileId);
        if (!profile) {
            continue;
        }

        const shiftStatus = row.shift_status ?? row.event_type ?? null;

        profile.shift_status = shiftStatus;
        profile.shift_resume_at = Object.hasOwn(row, "shift_resume_at")
            ? row.shift_resume_at
            : null;
        profile.recent_shift_event_at = Object.hasOwn(row, "recent_shift_event_at")
            ? row.recent_shift_event_at
            : null;
        profile.active_task = Object.hasOwn(row, "active_task")
            ? row.active_task
            : null;
        profile.paused_task = Object.hasOwn(row, "paused_task")
            ? row.paused_task
            : null;

        if (Object.hasOwn(row, "is_team")) {
            profile.is_team = row.is_team;
        }

        const assignerId =
            row?.active_task?.version?.user_profile_id_creator ||
            row?.paused_task?.version?.user_profile_id_creator;

        if (typeof getAssignerProfile === "function") {
            profile.assigner_info = assignerId
                ? getAssignerProfile(assignerId)
                : assignerId;
        }

        if (
            typeof isShiftRunning === "function" &&
            typeof isShiftPauseOrUnreachableOrInActive === "function"
        ) {
            profile.task_status = taskStop;
            if (profile.active_task && isShiftRunning(shiftStatus)) {
                profile.task_status = taskStart;
            } else if (isShiftPauseOrUnreachableOrInActive(shiftStatus)) {
                profile.task_status = taskPause;
            }
        }

        updated += 1;
    }

    return updated;
}

/**
 * @param {import('../Stores/User').useUserStore} userStore
 * @param {number|string} profileId
 * @param {{
 *   shift_status?: string|null,
 *   recent_shift_event_at?: string|null,
 *   shift_resume_at?: string|null,
 * }} fields
 */
export function mergeRemoteShiftFieldsOntoUserProfile(
    userStore,
    profileId,
    fields,
) {
    const profile =
        userStore.getUserProfileById(profileId) ??
        userStore.getUserProfile(profileId);
    if (!profile) {
        return;
    }

    if (fields.shift_status != null && fields.shift_status !== "") {
        profile.shift_status = fields.shift_status;
    }

    if (fields.recent_shift_event_at != null) {
        profile.recent_shift_event_at = fields.recent_shift_event_at;
    }

    if (Object.hasOwn(fields, "shift_resume_at")) {
        profile.shift_resume_at = fields.shift_resume_at;
    }
}

/**
 * @param {object} row
 * @param {number} profileId
 * @returns {boolean}
 */
function rowMatchesProfile(row, profileId) {
    const rowId = Number(row?.user_profile_id ?? row?.employee_id);

    return Number.isFinite(rowId) && rowId === profileId;
}

/**
 * @param {object[]|null|undefined} rows
 * @param {object} profile
 * @returns {{ rows: object[], changed: boolean }}
 */
export function patchWorklineRowsForProfile(rows, profile) {
    const profileId = Number(profile?.id ?? profile?.user_profile_id);
    if (!Array.isArray(rows) || !Number.isFinite(profileId)) {
        return { rows: rows ?? [], changed: false };
    }

    let changed = false;
    const nextRows = rows.map((row) => {
        if (!rowMatchesProfile(row, profileId)) {
            return row;
        }

        changed = true;

        return patchedWorklineRow(row, profile);
    });

    return { rows: nextRows, changed };
}

/**
 * Reassign workline arrays so Vue recomputes screens/rails that only
 * depend on the array identity.
 *
 * @param {{
 *   usr_profile_logged_time?: object[],
 *   shift_timer_division_groups?: object[],
 * }} appState
 * @param {object} profile
 * @returns {boolean}
 */
export function applyLiveSyncProfileShiftToWorkline(appState, profile) {
    if (!appState || !(profile?.id ?? profile?.user_profile_id)) {
        return false;
    }

    const flat = patchWorklineRowsForProfile(
        appState.usr_profile_logged_time,
        profile,
    );
    if (flat.changed) {
        appState.usr_profile_logged_time = flat.rows;
    }

    const groups = appState.shift_timer_division_groups;
    if (!Array.isArray(groups) || !groups.length) {
        return flat.changed;
    }

    let groupsChanged = false;
    const nextGroups = groups.map((group) => {
        let groupChanged = false;
        let head = group?.head ?? null;

        if (head) {
            const patchedHead = patchWorklineRowsForProfile([head], profile);
            if (patchedHead.changed) {
                head = patchedHead.rows[0];
                groupChanged = true;
            }
        }

        const membersResult = patchWorklineRowsForProfile(
            group?.members,
            profile,
        );
        if (membersResult.changed) {
            groupChanged = true;
        }

        if (!groupChanged) {
            return group;
        }

        groupsChanged = true;

        return {
            ...group,
            head,
            members: membersResult.changed
                ? membersResult.rows
                : group.members,
        };
    });

    if (groupsChanged) {
        appState.shift_timer_division_groups = nextGroups;
    }

    return flat.changed || groupsChanged;
}
